/**
 * Formula Dependency Tracker
 *
 * Tracks dependencies between cells in the Excel grid to:
 * - Detect circular references
 * - Determine calculation order
 * - Enable smart recalculation
 * - Provide dependency visualization
 */

export interface CellRef {
  row: number;
  col: number;
}

export interface CellAddress {
  row: number;
  col: number;
  address: string; // e.g., "A1"
}

export interface DependencyNode {
  cell: CellAddress;
  formula: string;
  dependencies: CellAddress[]; // Cells this formula depends on
  dependents: CellAddress[]; // Cells that depend on this cell
}

export interface CircularReference {
  cycle: CellAddress[];
  message: string;
}

class DependencyTracker {
  private graph: Map<string, DependencyNode>;
  private calculationOrder: CellAddress[];
  private circularReferences: CircularReference[];

  constructor() {
    this.graph = new Map();
    this.calculationOrder = [];
    this.circularReferences = [];
  }

  /**
   * Convert column letter to index (A=0, B=1, ..., Z=25, AA=26, etc.)
   */
  private columnLetterToIndex(letter: string): number {
    let index = 0;
    for (let i = 0; i < letter.length; i++) {
      index = index * 26 + (letter.charCodeAt(i) - 65 + 1);
    }
    return index - 1;
  }

  /**
   * Convert column index to letter (0=A, 1=B, ..., 25=Z, 26=AA, etc.)
   */
  private columnIndexToLetter(index: number): string {
    let letter = '';
    let num = index + 1;
    while (num > 0) {
      const remainder = (num - 1) % 26;
      letter = String.fromCharCode(65 + remainder) + letter;
      num = Math.floor((num - 1) / 26);
    }
    return letter;
  }

  /**
   * Get cell address string from row/col indices
   */
  private getCellAddress(row: number, col: number): string {
    return `${this.columnIndexToLetter(col)}${row + 1}`;
  }

  /**
   * Parse cell address string to row/col indices
   */
  private parseCellAddress(address: string): CellRef | null {
    const match = address.match(/^([A-Z]+)(\d+)$/i);
    if (!match) return null;

    const col = this.columnLetterToIndex(match[1].toUpperCase());
    const row = parseInt(match[2]) - 1;

    return { row, col };
  }

  /**
   * Extract all cell references from a formula
   */
  private extractCellReferences(formula: string): CellAddress[] {
    const references: CellAddress[] = [];

    // Remove the = sign
    const expression = formula.startsWith('=') ? formula.substring(1) : formula;

    // Match cell references (A1, B2, etc.) and ranges (A1:A10)
    const cellPattern = /([A-Z]+)(\d+)(?::([A-Z]+)(\d+))?/gi;
    let match;

    while ((match = cellPattern.exec(expression)) !== null) {
      if (match[3] && match[4]) {
        // It's a range (e.g., A1:A10)
        const startCol = this.columnLetterToIndex(match[1].toUpperCase());
        const startRow = parseInt(match[2]) - 1;
        const endCol = this.columnLetterToIndex(match[3].toUpperCase());
        const endRow = parseInt(match[4]) - 1;

        // Add all cells in the range
        for (let row = startRow; row <= endRow; row++) {
          for (let col = startCol; col <= endCol; col++) {
            const address = this.getCellAddress(row, col);
            references.push({ row, col, address });
          }
        }
      } else {
        // Single cell reference
        const col = this.columnLetterToIndex(match[1].toUpperCase());
        const row = parseInt(match[2]) - 1;
        const address = this.getCellAddress(row, col);
        references.push({ row, col, address });
      }
    }

    return references;
  }

  /**
   * Add or update a cell's formula in the dependency graph
   */
  public addFormula(row: number, col: number, formula: string): void {
    const address = this.getCellAddress(row, col);
    const cellAddress: CellAddress = { row, col, address };

    // Extract dependencies from the formula
    const dependencies = this.extractCellReferences(formula);

    // Remove duplicates
    const uniqueDependencies = Array.from(
      new Map(dependencies.map((dep) => [dep.address, dep])).values()
    );

    // Get or create the node
    let node = this.graph.get(address);
    if (!node) {
      node = {
        cell: cellAddress,
        formula,
        dependencies: uniqueDependencies,
        dependents: [],
      };
      this.graph.set(address, node);
    } else {
      // Update existing node
      // First, remove this cell from the dependents list of old dependencies
      node.dependencies.forEach((dep) => {
        const depNode = this.graph.get(dep.address);
        if (depNode) {
          depNode.dependents = depNode.dependents.filter((d) => d.address !== address);
        }
      });

      node.formula = formula;
      node.dependencies = uniqueDependencies;
    }

    // Add this cell to the dependents list of each dependency
    uniqueDependencies.forEach((dep) => {
      let depNode = this.graph.get(dep.address);
      if (!depNode) {
        // Create a placeholder node for cells that don't have formulas
        depNode = {
          cell: dep,
          formula: '',
          dependencies: [],
          dependents: [],
        };
        this.graph.set(dep.address, depNode);
      }

      // Add to dependents if not already present
      if (!depNode.dependents.some((d) => d.address === address)) {
        depNode.dependents.push(cellAddress);
      }
    });

    // Recalculate the calculation order
    this.calculateOrder();
  }

  /**
   * Remove a cell from the dependency graph
   */
  public removeFormula(row: number, col: number): void {
    const address = this.getCellAddress(row, col);
    const node = this.graph.get(address);

    if (!node) return;

    // Remove this cell from the dependents list of its dependencies
    node.dependencies.forEach((dep) => {
      const depNode = this.graph.get(dep.address);
      if (depNode) {
        depNode.dependents = depNode.dependents.filter((d) => d.address !== address);
      }
    });

    // If this cell has no dependents and no formula, remove it completely
    if (node.dependents.length === 0) {
      this.graph.delete(address);
    } else {
      // Just clear the formula and dependencies
      node.formula = '';
      node.dependencies = [];
    }

    // Recalculate the calculation order
    this.calculateOrder();
  }

  /**
   * Detect circular references using depth-first search
   */
  private detectCircularReferences(): CircularReference[] {
    const circular: CircularReference[] = [];
    const visited = new Set<string>();
    const recursionStack = new Set<string>();
    const currentPath: CellAddress[] = [];

    const dfs = (address: string): boolean => {
      visited.add(address);
      recursionStack.add(address);

      const node = this.graph.get(address);
      if (!node) return false;

      currentPath.push(node.cell);

      for (const dep of node.dependencies) {
        if (!visited.has(dep.address)) {
          if (dfs(dep.address)) {
            return true;
          }
        } else if (recursionStack.has(dep.address)) {
          // Found a cycle
          const cycleStartIndex = currentPath.findIndex((c) => c.address === dep.address);
          const cycle = currentPath.slice(cycleStartIndex);
          cycle.push(dep); // Close the cycle

          circular.push({
            cycle,
            message: `Circular reference detected: ${cycle.map((c) => c.address).join(' → ')}`,
          });
          return true;
        }
      }

      currentPath.pop();
      recursionStack.delete(address);
      return false;
    };

    // Check each node
    for (const address of this.graph.keys()) {
      if (!visited.has(address)) {
        dfs(address);
      }
    }

    return circular;
  }

  /**
   * Calculate the correct evaluation order using topological sort
   */
  private calculateOrder(): void {
    // First, detect circular references
    this.circularReferences = this.detectCircularReferences();

    if (this.circularReferences.length > 0) {
      // If there are circular references, we can't determine a valid order
      this.calculationOrder = [];
      return;
    }

    const order: CellAddress[] = [];
    const visited = new Set<string>();
    const tempMarked = new Set<string>();

    const visit = (address: string): void => {
      if (tempMarked.has(address)) {
        // This shouldn't happen if circular reference detection works correctly
        return;
      }

      if (visited.has(address)) {
        return;
      }

      tempMarked.add(address);

      const node = this.graph.get(address);
      if (node) {
        // Visit all dependencies first
        for (const dep of node.dependencies) {
          visit(dep.address);
        }

        // Only add nodes with formulas to the calculation order
        if (node.formula) {
          order.push(node.cell);
        }
      }

      tempMarked.delete(address);
      visited.add(address);
    };

    // Visit all nodes
    for (const address of this.graph.keys()) {
      if (!visited.has(address)) {
        visit(address);
      }
    }

    this.calculationOrder = order;
  }

  /**
   * Get the calculation order for all formulas
   */
  public getCalculationOrder(): CellAddress[] {
    return this.calculationOrder;
  }

  /**
   * Get circular references
   */
  public getCircularReferences(): CircularReference[] {
    return this.circularReferences;
  }

  /**
   * Get all cells that depend on the given cell (directly or indirectly)
   */
  public getDependentCells(row: number, col: number): CellAddress[] {
    const address = this.getCellAddress(row, col);
    const dependents = new Set<string>();
    const queue: string[] = [address];

    while (queue.length > 0) {
      const current = queue.shift()!;
      const node = this.graph.get(current);

      if (node) {
        for (const dependent of node.dependents) {
          if (!dependents.has(dependent.address)) {
            dependents.add(dependent.address);
            queue.push(dependent.address);
          }
        }
      }
    }

    return Array.from(dependents).map((addr) => {
      const node = this.graph.get(addr);
      return node!.cell;
    });
  }

  /**
   * Get all cells that the given cell depends on (directly or indirectly)
   */
  public getDependencyCells(row: number, col: number): CellAddress[] {
    const address = this.getCellAddress(row, col);
    const dependencies = new Set<string>();
    const queue: string[] = [address];

    while (queue.length > 0) {
      const current = queue.shift()!;
      const node = this.graph.get(current);

      if (node) {
        for (const dependency of node.dependencies) {
          if (!dependencies.has(dependency.address)) {
            dependencies.add(dependency.address);
            queue.push(dependency.address);
          }
        }
      }
    }

    return Array.from(dependencies).map((addr) => {
      const node = this.graph.get(addr);
      return node!.cell;
    });
  }

  /**
   * Get dependency information for a cell
   */
  public getCellInfo(row: number, col: number): DependencyNode | null {
    const address = this.getCellAddress(row, col);
    return this.graph.get(address) || null;
  }

  /**
   * Check if there are any circular references
   */
  public hasCircularReferences(): boolean {
    return this.circularReferences.length > 0;
  }

  /**
   * Get the complete dependency graph
   */
  public getGraph(): Map<string, DependencyNode> {
    return new Map(this.graph);
  }

  /**
   * Clear all dependencies
   */
  public clear(): void {
    this.graph.clear();
    this.calculationOrder = [];
    this.circularReferences = [];
  }

  /**
   * Export the dependency graph for debugging
   */
  public exportGraph(): string {
    const nodes: any[] = [];

    this.graph.forEach((node, address) => {
      nodes.push({
        cell: address,
        formula: node.formula,
        dependencies: node.dependencies.map((d) => d.address),
        dependents: node.dependents.map((d) => d.address),
      });
    });

    return JSON.stringify(
      {
        nodes,
        calculationOrder: this.calculationOrder.map((c) => c.address),
        circularReferences: this.circularReferences.map((cr) => ({
          cycle: cr.cycle.map((c) => c.address),
          message: cr.message,
        })),
      },
      null,
      2
    );
  }
}

export default DependencyTracker;
